// 관리자 — 신규 개원 치과 (2026-09-03 대표 지시)
// 심평원(HIRA) 병원정보에서 개설일이 최근인 치과를 매일 자동 수집(newClinicLeads). 여기서는 목록·전화·주소 확인,
// 처리 표시·메모만 한다. 데이터 생성은 서버(CF) 전용, 관리자는 handled/memo/assignedLabName만 쓸 수 있다(rules).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc, query, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../../firebase/config';
import { Building2, RefreshCw, Phone, MapPin, Check, Undo2 } from 'lucide-react';

const DAY = 86400000;
const toDate = (t) => t ? (typeof t.toDate === 'function' ? t.toDate() : new Date(t)) : null;
const fmt = (d) => d ? `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}` : '-';
const fmtTs = (t) => { const d = toDate(t); return d ? `${fmt(d)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '-'; };

function AdminNewClinics() {
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [days, setDays] = useState(30);             // 개원일 기준 최근 N일
    const [region, setRegion] = useState('');          // 시도/시군구/주소 검색
    const [showHandled, setShowHandled] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncMsg, setSyncMsg] = useState('');
    const [memoDraft, setMemoDraft] = useState({});    // { id: text }

    const load = useCallback(async () => {
        setLoading(true); setLoadError('');
        try {
            const [snap, metaSnap] = await Promise.all([
                getDocs(query(collection(db, 'newClinicLeads'), orderBy('openedAt', 'desc'), limit(1000))),
                getDoc(doc(db, 'newClinicLeadsMeta', 'status')),
            ]);
            setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setMeta(metaSnap.exists() ? metaSnap.data() : null);
        } catch (e) {
            console.error('[신규치과] 목록 조회 실패:', e);
            setLoadError(e?.message || '목록을 불러오지 못했습니다');
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const runSync = async () => {
        if (syncing) return;
        setSyncing(true); setSyncMsg('심평원에서 전국 치과 목록을 훑는 중… (1~3분)');
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'adminSyncNewClinics', { timeout: 540000 });
            const r = await fn({ days: 120 });
            const d = r.data || {};
            setSyncMsg(`완료 — 확인 ${d.scanned ?? 0}곳 중 최근 120일 개원 ${d.matched ?? 0}곳${d.errors?.length ? ` · 오류 ${d.errors.length}건` : ''}`);
            await load();
        } catch (e) {
            console.error('[신규치과] 수동 수집 실패:', e);
            setSyncMsg('실패: ' + (e?.message || e));
        } finally { setSyncing(false); }
    };

    const setHandled = async (row, handled) => {
        try {
            await updateDoc(doc(db, 'newClinicLeads', row.id), {
                handled, handledAt: handled ? serverTimestamp() : null, handledBy: handled ? (auth.currentUser?.uid || null) : null,
            });
            setRows(prev => prev.map(r => r.id === row.id ? { ...r, handled } : r));
        } catch (e) { console.error('[신규치과] 처리 표시 실패:', e); alert('저장 실패: ' + (e?.message || e)); }
    };
    const saveMemo = async (row) => {
        const memo = (memoDraft[row.id] ?? row.memo ?? '').trim();
        try {
            await updateDoc(doc(db, 'newClinicLeads', row.id), { memo });
            setRows(prev => prev.map(r => r.id === row.id ? { ...r, memo } : r));
            setMemoDraft(prev => { const n = { ...prev }; delete n[row.id]; return n; });
        } catch (e) { console.error('[신규치과] 메모 저장 실패:', e); alert('저장 실패: ' + (e?.message || e)); }
    };

    const filtered = useMemo(() => {
        const since = Date.now() - days * DAY;
        const q = region.trim();
        return rows.filter(r => {
            const o = toDate(r.openedAt);
            if (!o || o.getTime() < since) return false;
            if (!showHandled && r.handled) return false;
            if (q && !(`${r.sido} ${r.sggu} ${r.addr} ${r.name}`).includes(q)) return false;
            return true;
        });
    }, [rows, days, region, showHandled]);

    const handledCount = rows.filter(r => r.handled).length;

    return (
        <div style={S.page}>
            <div style={S.head}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Building2 size={22} color="#6366f1" />
                    <h1 style={S.h1}>신규 개원 치과</h1>
                </div>
                <button onClick={runSync} disabled={syncing} style={{ ...S.btnP, opacity: syncing ? 0.6 : 1 }}>
                    <RefreshCw size={14} /> {syncing ? '가져오는 중…' : '지금 가져오기'}
                </button>
            </div>
            <div style={S.sub}>
                심평원(건강보험심사평가원) 병원정보에서 개설일이 최근인 치과를 매일 아침 6:30에 자동으로 모읍니다.
                심평원 등록이 개원보다 며칠~몇 주 늦을 수 있습니다.
                {meta && (
                    <span style={{ marginLeft: 8, color: '#94a3b8' }}>
                        · 마지막 수집 {fmtTs(meta.lastSyncAt)}{meta.running ? ' (진행 중)' : ''}
                        {meta.lastError ? <span style={{ color: '#dc2626' }}> · 오류: {meta.lastError}</span> : ''}
                    </span>
                )}
            </div>
            {syncMsg && <div style={S.notice}>{syncMsg}</div>}
            {loadError && <div style={{ ...S.notice, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>{loadError}</div>}

            <div style={S.toolbar}>
                <span style={S.label}>개원일</span>
                {[7, 30, 60, 120].map(n => (
                    <button key={n} onClick={() => setDays(n)} style={{ ...S.chip, ...(days === n ? S.chipOn : {}) }}>최근 {n}일</button>
                ))}
                <input value={region} onChange={e => setRegion(e.target.value)} placeholder="지역·주소·치과명 검색 (예: 인천, 연수구)" style={S.input} />
                <label style={S.check}>
                    <input type="checkbox" checked={showHandled} onChange={e => setShowHandled(e.target.checked)} style={{ width: 'auto' }} />
                    처리한 곳도 보기 ({handledCount})
                </label>
                <span style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>{filtered.length}곳</span>
            </div>

            {loading ? (
                <div style={S.empty}>불러오는 중…</div>
            ) : filtered.length === 0 ? (
                <div style={S.empty}>
                    {rows.length === 0
                        ? '아직 수집된 치과가 없습니다. [지금 가져오기]를 눌러 주세요.'
                        : '조건에 맞는 치과가 없습니다. 기간을 늘리거나 검색어를 지워 보세요.'}
                </div>
            ) : (
                <div style={S.tableWrap}>
                    <table style={S.table}>
                        <thead>
                            <tr>
                                <th style={S.th}>개원일</th>
                                <th style={S.th}>치과</th>
                                <th style={S.th}>전화</th>
                                <th style={S.th}>주소</th>
                                <th style={S.th}>메모</th>
                                <th style={S.th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(r => {
                                const o = toDate(r.openedAt);
                                const ageDays = o ? Math.floor((Date.now() - o.getTime()) / DAY) : null;
                                const draft = memoDraft[r.id];
                                return (
                                    <tr key={r.id} style={{ opacity: r.handled ? 0.55 : 1 }}>
                                        <td style={S.td}>
                                            <div style={{ fontWeight: 700 }}>{fmt(o)}</div>
                                            <div style={S.small}>{ageDays !== null ? `${ageDays}일 전` : ''}</div>
                                        </td>
                                        <td style={S.td}>
                                            <div style={{ fontWeight: 700, color: '#0f172a' }}>{r.name}</div>
                                            <div style={S.small}>{r.kind}{r.drTotCnt ? ` · 의사 ${r.drTotCnt}명` : ''}</div>
                                        </td>
                                        <td style={S.td}>
                                            {r.tel ? <a href={`tel:${r.tel}`} style={S.tel}><Phone size={12} /> {r.tel}</a> : <span style={S.small}>없음</span>}
                                        </td>
                                        <td style={S.td}>
                                            <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                                                <MapPin size={12} color="#94a3b8" style={{ marginTop: 3, flexShrink: 0 }} />
                                                <div>
                                                    <div>{r.addr}</div>
                                                    <div style={S.small}>{r.sido} {r.sggu}{r.postNo ? ` · 우 ${r.postNo}` : ''}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={S.td}>
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <input value={draft ?? r.memo ?? ''} onChange={e => setMemoDraft(p => ({ ...p, [r.id]: e.target.value }))}
                                                    onKeyDown={e => { if (e.key === 'Enter') saveMemo(r); }}
                                                    placeholder="통화 결과 등" style={S.memo} />
                                                {draft !== undefined && draft !== (r.memo || '') && (
                                                    <button onClick={() => saveMemo(r)} style={S.btnMini}>저장</button>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                                            {r.handled ? (
                                                <button onClick={() => setHandled(r, false)} style={S.btnMini}><Undo2 size={12} /> 되돌리기</button>
                                            ) : (
                                                <button onClick={() => setHandled(r, true)} style={S.btnDone}><Check size={12} /> 처리함</button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

const S = {
    page: { padding: 20, maxWidth: 1200, margin: '0 auto' },
    head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
    h1: { fontSize: 20, fontWeight: 800, margin: 0, color: '#0f172a' },
    sub: { fontSize: 12.5, color: '#64748b', margin: '6px 0 14px', lineHeight: 1.6 },
    notice: { background: '#EEF2FF', border: '1px solid #C7D2FE', color: '#3730A3', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 },
    toolbar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
    label: { fontSize: 12.5, fontWeight: 700, color: '#475569' },
    chip: { padding: '6px 11px', borderRadius: 999, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
    chipOn: { background: '#6366f1', borderColor: '#6366f1', color: '#fff' },
    input: { flex: 1, minWidth: 220, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' },
    check: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: '#475569', cursor: 'pointer' },
    empty: { padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13.5, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12 },
    tableWrap: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflowX: 'auto' },
    table: { width: '100%', minWidth: 880, borderCollapse: 'collapse' },
    th: { textAlign: 'left', fontSize: 11.5, fontWeight: 700, color: '#94a3b8', padding: '9px 12px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', whiteSpace: 'nowrap' },
    td: { padding: '10px 12px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
    small: { fontSize: 11.5, color: '#94a3b8', marginTop: 2 },
    tel: { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#4f46e5', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' },
    memo: { width: 180, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit' },
    btnP: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 15px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
    btnMini: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 9px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
    btnDone: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 11px', borderRadius: 6, border: 'none', background: '#0ca30c', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
};

export default AdminNewClinics;
